import { FC, useEffect, useRef, useState } from "react";
import { Polyline } from "@react-google-maps/api";
import axios from "axios";
import { OrderStatus } from "../../../../types/order/IOrder";

import { MapPolylineProps } from "../types";

// -----------------------------------------------------------------------------
// MapPolyline (enhanced with completed route history)
// -----------------------------------------------------------------------------
// Renders the complete route with completed portions greyed out and remaining
// portions in the active color. Always shows driver history.
// -----------------------------------------------------------------------------

export const MapPolyline: FC<MapPolylineProps> = ({ selectedRoute }) => {
  /* ------------------------------------------------------------------------ */
  /* Refs                                                                    */
  /* ------------------------------------------------------------------------ */
  const completedPolylineRef = useRef<google.maps.Polyline | null>(null);
  const remainingPolylineRef = useRef<google.maps.Polyline | null>(null);
  const animationTimerRef = useRef<NodeJS.Timeout | null>(null);

  /* ------------------------------------------------------------------------ */
  /* State                                                                   */
  /* ------------------------------------------------------------------------ */
  const [completedPath, setCompletedPath] = useState<google.maps.LatLngLiteral[]>([]);
  const [remainingPath, setRemainingPath] = useState<google.maps.LatLngLiteral[]>([]);
  const [remainingDisplayedPath, setRemainingDisplayedPath] = useState<google.maps.LatLngLiteral[]>([]);
  const [polylineKey, setPolylineKey] = useState(0);

  /* ------------------------------------------------------------------------ */
  /* Helpers                                                                  */
  /* ------------------------------------------------------------------------ */
  // Split route into completed and remaining segments
  const splitRoute = (route: any[]) => {
    if (!route || route.length < 2) return { completed: [], remaining: [] };

    // Find the index of the first non-completed stop
    const firstRemainingIndex = route.findIndex(stop => 
      stop.status !== OrderStatus.Completed
    );

    if (firstRemainingIndex === -1) {
      // All stops are completed
      return { completed: route, remaining: [] };
    } else if (firstRemainingIndex === 0) {
      // No stops are completed
      return { completed: [], remaining: route };
    } else {
      // Only include actually completed stops in completed path
      // The remaining path will handle continuity from last completed to next stop
      return {
        completed: route.slice(0, firstRemainingIndex),
        remaining: route.slice(firstRemainingIndex - 1) // Include last completed for continuity
      };
    }
  };

  // Fetch route from Google Routes API
  const fetchRouteSegment = async (
    coordinates: google.maps.LatLngLiteral[]
  ): Promise<google.maps.LatLngLiteral[]> => {
    if (coordinates.length < 2) return coordinates;

    try {
      const response = await axios.post(
        "https://routes.googleapis.com/directions/v2:computeRoutes",
        {
          origin: {
            location: {
              latLng: {
                latitude: coordinates[0].lat,
                longitude: coordinates[0].lng,
              },
            },
          },
          destination: {
            location: {
              latLng: {
                latitude: coordinates[coordinates.length - 1].lat,
                longitude: coordinates[coordinates.length - 1].lng,
              },
            },
          },
          intermediates: coordinates.slice(1, -1).map((coord) => ({
            location: {
              latLng: {
                latitude: coord.lat,
                longitude: coord.lng,
              },
            },
          })),
          travelMode: "DRIVE",
        },
        {
          headers: {
            "Content-Type": "application/json",
            "X-Goog-Api-Key": process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
            "X-Goog-FieldMask": "routes.polyline",
          },
        }
      );

      const encodedPolyline = response.data?.routes?.[0]?.polyline?.encodedPolyline;
      if (!encodedPolyline) return coordinates;

      return window.google.maps.geometry.encoding
        .decodePath(encodedPolyline)
        .map((point) => ({ lat: point.lat(), lng: point.lng() }));
    } catch (error) {
      console.error("Failed to compute route segment:", error);
      return coordinates;
    }
  };

  // Start snake animation for remaining path
  const startSnakeAnimation = (fullPath: google.maps.LatLngLiteral[]): void => {
    // Clear any existing timer
    if (animationTimerRef.current) {
      clearInterval(animationTimerRef.current);
    }

    // Reset UI state
    setRemainingDisplayedPath([]);

    if (fullPath.length < 2) return;

    const animationDurationMs = 1500;
    const fps = 60;
    const totalFrames = (animationDurationMs / 1000) * fps;
    let frame = 0;

    animationTimerRef.current = setInterval(() => {
      frame += 1;
      const progress = Math.min(frame / totalFrames, 1);
      const currentIndex = Math.floor(fullPath.length * progress);
      setRemainingDisplayedPath(fullPath.slice(0, Math.max(2, currentIndex)));

      if (progress === 1) {
        clearInterval(animationTimerRef.current as NodeJS.Timeout);
        animationTimerRef.current = null;
      }
    }, 1000 / fps);
  };

  /* ------------------------------------------------------------------------ */
  /* Effects                                                                  */
  /* ------------------------------------------------------------------------ */
  useEffect(() => {
    // Clean up existing polylines & animation
    if (completedPolylineRef.current) {
      completedPolylineRef.current.setMap(null);
      completedPolylineRef.current = null;
    }
    if (remainingPolylineRef.current) {
      remainingPolylineRef.current.setMap(null);
      remainingPolylineRef.current = null;
    }
    if (animationTimerRef.current) {
      clearInterval(animationTimerRef.current);
      animationTimerRef.current = null;
    }

    setCompletedPath([]);
    setRemainingPath([]);
    setRemainingDisplayedPath([]);
    setPolylineKey((k) => k + 1);

    if (!selectedRoute?.route) return;

    const coordinates = selectedRoute.route
      .filter((stop) => stop.location.coordinate?.lat && stop.location.coordinate?.lon)
      .map((stop) => ({
        lat: stop.location.coordinate!.lat!,
        lng: stop.location.coordinate!.lon!,
      }));

    if (coordinates.length < 2) return;

    const { completed, remaining } = splitRoute(selectedRoute.route);

    // Process completed segment
    if (completed.length >= 2) {
      const completedCoords = completed
        .filter((stop) => stop.location.coordinate?.lat && stop.location.coordinate?.lon)
        .map((stop) => ({
          lat: stop.location.coordinate!.lat!,
          lng: stop.location.coordinate!.lon!,
        }));

      if (completedCoords.length >= 2) {
        fetchRouteSegment(completedCoords).then(setCompletedPath);
      }
    }

    // Process remaining segment
    if (remaining.length >= 2) {
      const remainingCoords = remaining
        .filter((stop) => stop.location.coordinate?.lat && stop.location.coordinate?.lon)
        .map((stop) => ({
          lat: stop.location.coordinate!.lat!,
          lng: stop.location.coordinate!.lon!,
        }));

      if (remainingCoords.length >= 2) {
        fetchRouteSegment(remainingCoords).then((path) => {
          setRemainingPath(path);
          startSnakeAnimation(path);
        });
      }
    }

    // Cleanup when component unmounts
    return () => {
      if (completedPolylineRef.current) {
        completedPolylineRef.current.setMap(null);
        completedPolylineRef.current = null;
      }
      if (remainingPolylineRef.current) {
        remainingPolylineRef.current.setMap(null);
        remainingPolylineRef.current = null;
      }
      if (animationTimerRef.current) {
        clearInterval(animationTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRoute?.id, selectedRoute?.route]);

  /* ------------------------------------------------------------------------ */
  /* Render                                                                   */
  /* ------------------------------------------------------------------------ */
  return (
    <>
      {/* Completed route segment - always visible, greyed out */}
      {completedPath.length >= 2 && (
        <Polyline
          key={`completed-polyline-${polylineKey}`}
          path={completedPath}
          onLoad={(poly) => {
            completedPolylineRef.current = poly;
          }}
          onUnmount={(poly) => {
            poly.setMap(null);
            if (completedPolylineRef.current === poly) completedPolylineRef.current = null;
          }}
          options={{
            strokeColor: "#6B7280", // gray-500 - darker gray for better visibility on light maps
            strokeOpacity: 0.6,
            strokeWeight: 3,
            zIndex: 100, // Lower z-index for completed routes
          }}
        />
      )}

      {/* Remaining route segment - animated, active color */}
      {remainingDisplayedPath.length >= 2 && (
        <Polyline
          key={`remaining-polyline-${polylineKey}`}
          path={remainingDisplayedPath}
          onLoad={(poly) => {
            remainingPolylineRef.current = poly;
          }}
          onUnmount={(poly) => {
            poly.setMap(null);
            if (remainingPolylineRef.current === poly) remainingPolylineRef.current = null;
          }}
          options={{
            strokeColor: "#7214FF", // logibud blue-300 - active color
            strokeOpacity: 0.9,
            strokeWeight: 4,
            zIndex: 500, // Higher z-index for active routes - always on top
          }}
        />
      )}
    </>
  );
}; 